import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Gift, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const stockOf = (product) => {
  const variants = product.variants || [];
  return variants.length
    ? Math.max(...variants.map((variant) => Number(variant.stock || 0)))
    : Number(product.stock || 0);
};

const SharedWishlist = () => {
  const { token } = useParams();
  const { add } = useCart();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setWishlist(null);
    setError("");
    api.get(`/wishlist/shared/${encodeURIComponent(token)}`)
      .then((response) => setWishlist(response.data))
      .catch(() => setError("This wishlist link is unavailable or has been revoked."));
  }, [token]);

  const addToCart = (product) => {
    if (product.product_type === "gift_box" || (product.variants || []).length > 1) {
      navigate(`/product/${product.slug || product.id}`);
      toast.message("Choose options on the product page first");
      return;
    }
    if (add(product, product.variants?.[0] || null)) {
      toast.success("Added to your cart");
    }
  };

  if (error) {
    return (
      <div className="px-4 py-24 text-center" data-testid="shared-wishlist-error">
        <p className="text-[#64748B]">{error}</p>
        <Link to="/shop" className="mt-5 inline-block font-semibold text-[#C86D42]">
          Explore Eira products →
        </Link>
      </div>
    );
  }
  if (!wishlist) {
    return (
      <div className="py-24 text-center text-[#64748B]" data-testid="shared-wishlist-loading">
        Loading wishlist…
      </div>
    );
  }

  return (
    <div data-testid="shared-wishlist-page">
      <section className="border-b border-[#E2DCD2] bg-[#EBF2EE]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
          <div className="flex max-w-2xl items-start gap-4">
            <div
              className={[
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                "bg-[#2D5A47] text-white",
              ].join(" ")}
            >
              <Gift size={22} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8C6D46]">
                A little gift inspiration
              </p>
              <h1
                className="mt-2 font-serif text-4xl font-semibold text-[#1E2A24] sm:text-5xl"
                data-testid="shared-wishlist-title"
              >
                {wishlist.owner_name}&apos;s Wishlist
              </h1>
              <p className="mt-3 text-sm leading-6 text-[#52645B]">
                A handpicked collection of things they&apos;d love. Add a favourite to your own
                cart or explore the details.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-14 lg:px-12">
        {wishlist.products.length ? (
          <div
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="shared-wishlist-grid"
          >
            {wishlist.products.map((product) => {
              const stock = stockOf(product);
              const price = product.variants?.[0]?.price ?? product.price;
              const mrp = product.variants?.[0]?.mrp ?? product.mrp;
              const canAdd = product.available && stock > 0;
              return (
                <article
                  key={product.id}
                  className="flex flex-col overflow-hidden border border-[#E2DCD2] bg-white"
                  data-testid={`shared-wishlist-product-${product.id}`}
                >
                  <Link
                    to={`/product/${product.slug || product.id}`}
                    className="aspect-square overflow-hidden bg-[#F4F1EA]"
                    data-testid={`shared-wishlist-product-link-${product.id}`}
                  >
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className={[
                          "h-full w-full object-cover transition-transform duration-500",
                          "hover:scale-105",
                        ].join(" ")}
                      />
                    ) : (
                      <div
                        className="flex h-full items-center justify-center text-sm text-[#64748B]"
                      >
                        Eira
                      </div>
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col p-4">
                    <p
                      className={[
                        "text-[10px] font-semibold uppercase tracking-[0.18em]",
                        "text-[#8C6D46]",
                      ].join(" ")}
                    >
                      {product.brand}
                    </p>
                    <Link
                      to={`/product/${product.slug || product.id}`}
                      className={[
                        "mt-2 text-base font-semibold leading-6 text-[#1E2A24]",
                        "hover:text-[#C86D42]",
                      ].join(" ")}
                    >
                      {product.name}
                    </Link>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="text-lg font-bold text-[#2D5A47]">
                        {inr(price)}
                      </span>
                      {mrp > price && (
                        <span className="text-xs text-[#94A3B8] line-through">{inr(mrp)}</span>
                      )}
                    </div>
                    <p
                      className={[
                        "mt-2 text-xs font-semibold",
                        canAdd ? "text-[#2D5A47]" : "text-[#A63A2B]",
                      ].join(" ")}
                      data-testid={`shared-wishlist-availability-${product.id}`}
                    >
                      {canAdd ? "Available" : "Currently unavailable"}
                    </p>
                    <button
                      type="button"
                      disabled={!canAdd}
                      onClick={() => addToCart(product)}
                      className={[
                        "mt-5 inline-flex items-center justify-center gap-2",
                        "border border-[#2D5A47]",
                        "bg-[#2D5A47] px-3 py-2.5 text-sm font-semibold text-white",
                        "transition-colors hover:bg-[#214435] disabled:cursor-not-allowed",
                        "disabled:border-[#D6DAD4] disabled:bg-[#D6DAD4]",
                      ].join(" ")}
                      data-testid={`shared-wishlist-add-cart-${product.id}`}
                    >
                      <ShoppingBag size={16} />
                      {product.product_type === "gift_box" || (product.variants || []).length > 1
                        ? "Choose options"
                        : "Add to cart"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="py-20 text-center text-[#64748B]" data-testid="shared-wishlist-empty">
            This wishlist is currently empty.
          </div>
        )}
      </section>
    </div>
  );
};

export default SharedWishlist;